/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputToggleTextModel} from './models/input-toggle-text.model';
import {AbstractControl} from '@angular/forms';
import {InputMessagesService} from '../../../../../shared/input/service/input-messages.service';
import {WarningsEnum} from '../../../../../shared/warnings/warnings.enum';
import {InputTextComponent} from '../../../../../shared/input/input-text/input-text.component';
import {MandatoryMessageTypeEnum} from '../../../../../shared/input/service/mandatory-message-type.enum';
import {SwitchButtonModel} from '../../../../../shared/switch-button/models/switch-button.model';
import {InputToggleTextConfiguration} from './configuration/input-toggle-text.configuration';
import {BundlingSettingsInputsEmitterModel} from '../models/bundling-settings-inputs-emitter.model';

@Component({
    selector: 'app-input-toggle-text',
    templateUrl: './input-toggle-text.component.html',
    styleUrls: ['./input-toggle-text.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class InputToggleTextComponent implements AfterViewInit, OnChanges, OnInit {
    @Input() inputToggleTextConfiguration: InputToggleTextConfiguration;
    @Input() inputToggleTextModel: InputToggleTextModel;
    @Input() currencyType: string;
    @Input() disableInput: boolean;
    @Input() customWarningMessage: string;
    @Output() valueChange = new EventEmitter<BundlingSettingsInputsEmitterModel>();
    @ViewChild('inputText') textInput: InputTextComponent;
    textInputMessages = new Map<WarningsEnum, string[]>();
    switchButtonModel: SwitchButtonModel;

    constructor(private inputMessagesService: InputMessagesService,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.populateSwitchButton();
    }

    ngAfterViewInit(): void {
        this.checkCurrentInputStatus(this.inputToggleTextModel.toggleActive);
        this.cdr.detectChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['inputToggleTextModel']?.firstChange
                && (changes['inputToggleTextModel']?.previousValue !== changes['inputToggleTextModel']?.currentValue)) {
            this.populateSwitchButton();
            this.checkCurrentInputStatus(this.inputToggleTextModel?.toggleActive);
        }
        if (changes['customWarningMessage']) {
            this.inputMessagesService.customInputValueWarning(this.textInputMessages,
                    this.customWarningMessage);
        }
    }

    inputToggleTextDataCy(code: string): string {
        let dataCy = 'input-toggle-text';
        if (code != null) {
            dataCy += `-${code.toLowerCase()}`;
        }
        return dataCy;
    }

    toggleField(active: boolean, code: string, valueText: string): void {
        this.valueChange.emit({
            value: valueText,
            code: code,
            isValid: true,
            isDisabled: this.textInput.isDisabled
        });
        this.checkCurrentInputStatus(active);
    }

    manageInputTextChange(inputData: AbstractControl): void {
        const errorSampleText = this.inputToggleTextConfiguration.errorMessage ?? null;
        this.inputMessagesService.mandatoryWarnings(inputData,
                this.textInputMessages,
                MandatoryMessageTypeEnum.APPLY);
        this.inputMessagesService.invalidInputValueError(inputData,
                this.textInputMessages,
                this.inputToggleTextConfiguration.toggleButtonConfiguration.label, errorSampleText);
        this.textInputMessages = new Map(this.textInputMessages);
    }

    setInputTextValue(value: string, code: string): void {
        this.valueChange.emit({
            value: value,
            code: code,
            isValid: value === undefined || (this.textInput?.isValid && value !== '' && value != null),
            isDisabled: this.textInput.isDisabled
        });
    }

    private checkCurrentInputStatus(active: boolean): void {
        if (!active) {
            this.textInput?.setValue(undefined);
            this.textInput?.disable();
        } else {
            this.textInput?.enable();
            this.textInput?.setValue(this.inputToggleTextModel.value);
        }
    }

    private populateSwitchButton(): void {
        this.switchButtonModel = {
            checked: this.inputToggleTextModel.toggleActive,
            disabled: this.inputToggleTextModel.viewMode || this.inputToggleTextModel.toggleDisable
        };
    }
}
